import {
  PokemonType,
  PokemonTypeArray,
  SkillData,
  SkillEvent_AnchoredInvoke,
  SkillEvent_Vfx,
  SkillEvent_Wait,
  SkillRangeType,
  SkillType,
} from "types/battle-engine/battle";
import { parseSkillIdToName } from "../utils/parse-id-to-name";
import { msToTicks } from "../utils/ms-to-ticks";
import { CONE_3_PATTERN } from "../skill-patterns/cone-3";
import { CIRCLE_3_PATTERN } from "../skill-patterns/circle-3";
import { LINE_5_PATTERN } from "../skill-patterns/line-5";
import type { SkillCastPattern } from "../skill-patterns";

/** Client special-cases this path into a pulsing red Graphics square. */
export const TELEGRAPH_WARN_SPRITE = "__telegraph_warn__";

export const TELEGRAPH_TICKS = msToTicks(2000);
export const TELEGRAPH_POWER = 28;
export const TELEGRAPH_COOLDOWN = msToTicks(10000);
export const TELEGRAPH_MANA = 20;
export const TELEGRAPH_RANGE = 5;

const TILE_FX_BY_TYPE: Record<PokemonType, string> = {
  [PokemonType.NORMAL]: "/assets/spells/impact/melee-1-white.png",
  [PokemonType.FIRE]: "/assets/spells/tile-fx/fire-1.png",
  [PokemonType.WATER]: "/assets/spells/tile-fx/water-1.png",
  [PokemonType.GRASS]: "/assets/spells/tile-fx/grass-1.png",
  [PokemonType.ELECTRIC]: "/assets/spells/tile-fx/electric-1.png",
  [PokemonType.ICE]: "/assets/spells/tile-fx/ice-3.png",
  [PokemonType.FIGHTING]: "/assets/spells/tile-fx/red_punch_000__13.png",
  [PokemonType.POISON]: "/assets/spells/tile-fx/poison-4.png",
  [PokemonType.GROUND]: "/assets/spells/tile-fx/ground-3.png",
  [PokemonType.FLYING]: "/assets/spells/tile-fx/flying-3.png",
  [PokemonType.PSYCHIC]: "/assets/spells/tile-fx/psychic-3.png",
  [PokemonType.BUG]: "/assets/spells/tile-fx/bug-1.png",
  [PokemonType.ROCK]: "/assets/spells/tile-fx/rock-1.png",
  [PokemonType.GHOST]: "/assets/spells/tile-fx/dark-2.png",
  [PokemonType.DRAGON]: "/assets/spells/explosion/expfx1_stylized_explosion_C_large_blue.png",
  [PokemonType.DARK]: "/assets/spells/tile-fx/dark-3.png",
  [PokemonType.STEEL]: "/assets/spells/tile-fx/steel-3.png",
  [PokemonType.FAIRY]: "/assets/spells/tile-fx/magic-puff-pink.png",
};

/** Per-type hit-tile scale tweaks (default 1.2). */
const TILE_FX_HIT_SCALE_BY_TYPE: Partial<Record<PokemonType, number>> = {
  [PokemonType.GRASS]: 1.75,
  [PokemonType.ROCK]: 1.7,
};

const DEFAULT_TELEGRAPH_HIT_SCALE = 1.2;

const ANCHOR_CYCLE: Array<"caster" | "random_regions" | "all_enemies"> = [
  "caster",
  "random_regions",
  "all_enemies",
];

type PatternCells = Array<[number, number, boolean?]>;

const CHARGE_VFX_SPRITE = "/assets/spells/self/charge-1-white.png";

const buildTelegraphOnCast = (
  cells: PatternCells,
  hitSprite: string,
  telegraphTicks: number,
  power: number,
  hitScale = DEFAULT_TELEGRAPH_HIT_SCALE,
): SkillData["on_cast"] => {
  const chargeVfx: SkillEvent_Vfx = {
    __typename: "Vfx",
    duration_ticks: telegraphTicks,
    speed: 0.2,
    sprite_scale: 2,
    target: { type: "self", id: 0 },
    status_effects: [],
    starting_tick: 0,
    sprite_path: CHARGE_VFX_SPRITE,
    blocks_event_pipeline: false,
  };

  const warnTiles: SkillEvent_AnchoredInvoke[] = cells.map(([x, y]) => ({
    __typename: "AnchoredInvoke",
    duration_ticks: telegraphTicks,
    target: { type: "self", id: 0 },
    status_effects: [],
    starting_tick: 0,
    relative_x: x,
    relative_y: y,
    sprite_path: TELEGRAPH_WARN_SPRITE,
    sprite_scale: 1,
    anim_loops: 99,
    anim_speed: 0.5,
    damage: 0,
    blocks_event_pipeline: false,
  }));

  const wait: SkillEvent_Wait = {
    __typename: "Wait",
    duration_ticks: telegraphTicks,
    blocks_event_pipeline: true,
    target: { type: "self", id: 0 },
    status_effects: [],
    starting_tick: 0,
  };

  const hitTiles: SkillEvent_AnchoredInvoke[] = cells.map(([x, y]) => ({
    __typename: "AnchoredInvoke",
    duration_ticks: 50,
    target: { type: "self", id: 0 },
    status_effects: [],
    starting_tick: 0,
    relative_x: x,
    relative_y: y,
    sprite_path: hitSprite,
    sprite_scale: hitScale,
    anim_loops: 1,
    anim_speed: 1,
    damage: power,
    blocks_event_pipeline: false,
  }));

  return [chargeVfx, ...warnTiles, wait, ...hitTiles];
};

const typeSlug = (type: PokemonType) => type.toLowerCase();

const makeTelegraphSkill = (opts: {
  id: string;
  type: PokemonType;
  description: string;
  pattern: ReturnType<SkillCastPattern>;
  align: boolean;
  telegraph_anchor: "caster" | "random_regions" | "all_enemies";
}): SkillData => {
  const cells = opts.pattern.North as PatternCells;
  return {
    id: opts.id,
    name: parseSkillIdToName(opts.id),
    description: opts.description,
    power: TELEGRAPH_POWER,
    pokemon_type: opts.type,
    types: [SkillType.DAMAGE],
    range: { type: SkillRangeType.ANCHORED_INVOKE, value: TELEGRAPH_RANGE },
    cooldown_ticks: TELEGRAPH_COOLDOWN,
    mana_cost: TELEGRAPH_MANA,
    align_to_user_dir: opts.align,
    cast_pattern: opts.pattern,
    telegraph_anchor: opts.telegraph_anchor,
    telegraph_ticks: TELEGRAPH_TICKS,
    on_hit: [],
    on_cast: buildTelegraphOnCast(
      cells,
      TILE_FX_BY_TYPE[opts.type],
      TELEGRAPH_TICKS,
      TELEGRAPH_POWER,
      TILE_FX_HIT_SCALE_BY_TYPE[opts.type] ?? DEFAULT_TELEGRAPH_HIT_SCALE,
    ),
  };
};

/** Build warn+hit on_cast for arbitrary cell offsets (used by multi-anchor expand). */
export const buildTelegraphEventsForCells = (
  cells: Array<{ x: number; y: number }>,
  hitSprite: string,
  telegraphTicks: number,
  power: number,
  hitScale = DEFAULT_TELEGRAPH_HIT_SCALE,
): SkillData["on_cast"] => {
  const asTuples: PatternCells = cells.map((c) => [c.x, c.y]);
  return buildTelegraphOnCast(asTuples, hitSprite, telegraphTicks, power, hitScale);
};

export const TELEGRAPH_CONE_SKILLS: SkillData[] = PokemonTypeArray.map((type) =>
  makeTelegraphSkill({
    id: `telegraph-cone-${typeSlug(type)}`,
    type,
    description: `Telegraphs a frontal cone, then strikes with ${type.toLowerCase()} force.`,
    pattern: CONE_3_PATTERN(),
    align: true,
    telegraph_anchor: "caster",
  }),
);

export const TELEGRAPH_CIRCLE_SKILLS: SkillData[] = PokemonTypeArray.map(
  (type, index) =>
    makeTelegraphSkill({
      id: `telegraph-circle-${typeSlug(type)}`,
      type,
      description: `Telegraphs circular danger zones, then detonates with ${type.toLowerCase()} energy.`,
      pattern: CIRCLE_3_PATTERN(),
      align: false,
      telegraph_anchor: ANCHOR_CYCLE[index % ANCHOR_CYCLE.length]!,
    }),
);

export const TELEGRAPH_LINE_SKILLS: SkillData[] = PokemonTypeArray.map((type) =>
  makeTelegraphSkill({
    id: `telegraph-line-${typeSlug(type)}`,
    type,
    description: `Telegraphs a straight line, then blasts through with ${type.toLowerCase()} force.`,
    pattern: LINE_5_PATTERN(),
    align: true,
    telegraph_anchor: "caster",
  }),
);

export const ALL_TELEGRAPH_SKILLS: SkillData[] = [
  ...TELEGRAPH_CONE_SKILLS,
  ...TELEGRAPH_CIRCLE_SKILLS,
  ...TELEGRAPH_LINE_SKILLS,
];

/** Flat registry keyed by skill id for POKEMON_SKILLS merge. */
export const TELEGRAPH_SKILLS_BY_ID: Record<string, SkillData> =
  Object.fromEntries(ALL_TELEGRAPH_SKILLS.map((s) => [s.id, s]));
